import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import Cookies from 'js-cookie';
import { compose } from 'redux';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { connectAsync } from 'iguazu';
import { configureIguazuSSR, queryModuleWithData } from 'iguazu-holocron';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import DataManager from '@americanexpress/data-manager';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig, OnVisible } from '@americanexpress/data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import childRoutes from '../childRoutes';
import reducer from '../reducer';
import {
  getHelmetLinks,
  isPureRoute as getIsPureRoute,
  getOneIdentityCorrelationId,
  isAuthenticated,
  getDynatraceID,
  getActiveLocale,
} from '../selectors';
import { extendOnTrackCall } from '../utility';
import Scripts from './Scripts';
import { MODULE_NAME } from '../constants';

export const IdentityRoot = ({
  children,
  languageData,
  isPureRoute,
  helmetLinks,
  OneSeo,
  correlationId,
  authenticated,
  routes,
  dynatraceID,
  activeLocale,
}) => {
  const publicGuid = Cookies.get('blueboxpublic');
  const route = routes && routes.length > 0 && routes[routes.length - 1];
  const routeTitle = route && route.title;

  const {
    onTrackCall,
    ddlProviderProps,
    reactlyticsProps,
  } = getAmexDataManagerConfig({ extendOnTrackCall: extendOnTrackCall(correlationId) });

  return (
    <OnVisible analyticsInfo={{ element: MODULE_NAME }}>
      <OneSeo preventFetch={isPureRoute} />
      {!isPureRoute && (
        <Helmet
          title={routeTitle}
          meta={[
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'theme-color', content: '#006FCF' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
          ]}
          link={helmetLinks}
          htmlAttributes={{
            lang: activeLocale,
          }}
        />
      )}
      <DataManager
        onTrackCall={onTrackCall}
        ddlProviderProps={ddlProviderProps}
        reactlyticsProps={reactlyticsProps}
      >
        <AmexDDLRegister />
        <DDLRegister name="user.authenticated" value={authenticated || false} />
        {publicGuid && <DDLRegister name="user.publicGuid" value={publicGuid} />}
        <SharedScript
          name="one-stream-data-handler"
          windowCheck="oneStreamData"
          version="^0.1.2"
        />
        <Adobe version="^1.0.0" />
        {children}
      </DataManager>

      {
          !isPureRoute && (
          <Scripts
            hasClickToChat={languageData?.hasClickToChat}
            dynatraceID={dynatraceID}
          />
          )
        }
    </OnVisible>
  );
};

IdentityRoot.propTypes = {
  children: PropTypes.node.isRequired,
  languageData: PropTypes.shape({ hasClickToChat: PropTypes.bool }),
  OneSeo: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  isPureRoute: PropTypes.bool.isRequired,
  helmetLinks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  correlationId: PropTypes.string.isRequired,
  authenticated: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  activeLocale: PropTypes.string.isRequired,
  routes: PropTypes.shape({
    length: PropTypes.number,
  }),
  length: PropTypes.number,
  dynatraceID: PropTypes.string,
};

IdentityRoot.childRoutes = childRoutes;

export const loadDataAsProps = ({ store: { dispatch, getState } }) => ({
  OneSeo: () => dispatch(queryModuleWithData('axp-one-seo')),
  isPureRoute: () => ({ status: 'complete', data: getIsPureRoute(getState()) }),
  helmetLinks: () => ({ status: 'complete', data: getHelmetLinks(getState()) }),
  correlationId: () => ({ status: 'complete', data: getOneIdentityCorrelationId(getState()) }),
  authenticated: () => ({ status: 'complete', data: isAuthenticated(getState()) }),
  dynatraceID: () => ({ status: 'complete', data: getDynatraceID(getState()) }),
  activeLocale: () => ({ status: 'complete', data: getActiveLocale(getState()) }),
});

loadDataAsProps.ssr = true;

export const loadModuleData = async ({ store, module, ownProps }) => {
  const { dispatch } = store;
  await dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' }));
  await configureIguazuSSR({ store, module, ownProps });
};

IdentityRoot.holocron = {
  name: MODULE_NAME,
  reducer,
  loadModuleData,
};

const hocChain = compose(
  oneAppModuleWrapper(MODULE_NAME),
  connectAsync({ loadDataAsProps })
);

export default hocChain(IdentityRoot);
